import { useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { blogPosts } from '@shared/blog';
import BlogCard from '../components/BlogCard';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import { buildBreadcrumbJsonLd } from '../lib/seoSchema';
import { pageSeoMeta } from '@shared/pageSeoMeta';

const POSTS_PER_PAGE = 9;

export default function BlogIndexPage() {
  const { isDark } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  const totalPages = Math.max(1, Math.ceil(blogPosts.length / POSTS_PER_PAGE));
  const requestedPage = parseInt(searchParams.get('page') ?? '1', 10);
  const currentPage = Math.min(Math.max(1, isNaN(requestedPage) ? 1 : requestedPage), totalPages);

  const visiblePosts = useMemo(() => {
    const start = (currentPage - 1) * POSTS_PER_PAGE;
    return blogPosts.slice(start, start + POSTS_PER_PAGE);
  }, [currentPage]);

  const setPage = (n: number) => {
    if (n === 1) {
      searchParams.delete('page');
    } else {
      searchParams.set('page', String(n));
    }
    setSearchParams(searchParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const canonical = currentPage === 1 ? '/blog' : `/blog?page=${currentPage}`;

  const blogJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'ZeroSmoke Blog',
    description: 'Guides, comparisons and insights about nicotine pouches in India.',
    url: `https://zerosmoke.in${canonical}`,
    blogPost: visiblePosts.map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      url: `https://zerosmoke.in/blog/${p.slug}`,
      datePublished: p.publishedAt,
    })),
  };

  const breadcrumbJsonLd = buildBreadcrumbJsonLd([
    { name: 'Home', url: '/' },
    { name: 'Blog', url: '/blog' },
  ]);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
      <SEO
        title={`${pageSeoMeta.blog.title}${currentPage > 1 ? ` (Page ${currentPage})` : ''}`}
        description={pageSeoMeta.blog.description}
        canonical={canonical}
        jsonLd={[blogJsonLd, breadcrumbJsonLd]}
      />

      <Breadcrumb items={[
        { name: 'Home', href: '/' },
        { name: 'Blog' },
      ]} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-10 sm:mb-14 text-center"
        >
          <div className="inline-flex items-center gap-2 mb-3">
            <BookOpen size={20} className="text-emerald-500" />
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-500">
              ZeroSmoke Blog
            </span>
          </div>
          <h1 className={`text-4xl sm:text-5xl font-extrabold tracking-tight mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Nicotine Pouch Guides for India
          </h1>
          <p className={`text-base sm:text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Beginner explainers, brand deep-dives, comparisons and lifestyle articles — written for adult Indian readers in 2026.
          </p>
        </motion.div>

        {/* 3x3 Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {visiblePosts.map((post, i) => (
            <BlogCard key={post.slug} post={post} index={i} />
          ))}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
            className={`flex items-center gap-1 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
              currentPage === 1
                ? 'opacity-40 cursor-not-allowed'
                : 'hover:border-emerald-500 hover:text-emerald-500'
            } ${isDark ? 'border-gray-800 text-gray-300' : 'border-gray-200 text-gray-700'}`}
          >
            <ChevronLeft size={16} />
            <span className="hidden sm:inline">Previous</span>
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              onClick={() => setPage(n)}
              aria-label={`Page ${n}`}
              aria-current={n === currentPage ? 'page' : undefined}
              className={`min-w-[40px] px-3 py-2 rounded-lg border text-sm font-bold transition-all ${
                n === currentPage
                  ? 'bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-500/30'
                  : isDark
                    ? 'border-gray-800 text-gray-300 hover:border-emerald-500 hover:text-emerald-500'
                    : 'border-gray-200 text-gray-700 hover:border-emerald-500 hover:text-emerald-500'
              }`}
            >
              {n}
            </button>
          ))}

          <button
            onClick={() => setPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Next page"
            className={`flex items-center gap-1 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
              currentPage === totalPages
                ? 'opacity-40 cursor-not-allowed'
                : 'hover:border-emerald-500 hover:text-emerald-500'
            } ${isDark ? 'border-gray-800 text-gray-300' : 'border-gray-200 text-gray-700'}`}
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Page count info */}
        <div className={`text-center mt-6 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          Showing {(currentPage - 1) * POSTS_PER_PAGE + 1}–
          {Math.min(currentPage * POSTS_PER_PAGE, blogPosts.length)} of {blogPosts.length} articles
        </div>

        {/* CTA card */}
        <Link
          to="/store"
          className={`mt-14 block text-center rounded-2xl border p-8 transition-all hover:border-emerald-500 hover:shadow-lg ${
            isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'
          }`}
        >
          <p className={`text-sm uppercase tracking-widest font-bold text-emerald-500 mb-2`}>Ready to try?</p>
          <p className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Browse our nicotine pouch collection &rarr;
          </p>
        </Link>
      </section>
    </div>
  );
}
